/**
 * EventSponsors — the graded sponsor wall.
 *
 * Was a row of cards per level, and that was the wrong shape for the data.
 * Most levels hold one or two sponsors, so a row each meant four rows each
 * carrying a single logo against a full-width heading, and the panel read as
 * mostly empty however the cards were sized.
 *
 * It is now one centred composition in two sizes:
 *
 *   lead     level 1, large, on its own line. These are the sponsors who
 *            paid for the top of the page, and size is what says so.
 *
 *   rest     everything else at one size, flowing centred and wrapping.
 *            Sponsors sharing a level and a caption are bound into one
 *            block with the caption printed once above the pair, so
 *            "GOLD SPONSORS" is never set twice side by side.
 *
 * Centring is the whole fix: a lone logo lands in the middle of the panel and
 * reads as deliberate, where left-aligned it left a hole to its right.
 *
 * A sponsor with no logo has its name set as a wordmark on the plate rather
 * than an empty box with a caption under it — a missing upload should look
 * like a type treatment, not a broken image.
 *
 * An event where nobody set a level or a caption is one unheaded flow of
 * plates. That is every event written before these columns existed, so it is
 * the case that has to be right.
 */

import React from 'react';
import { headingKey, parseLevel } from '../sponsorHeadings';
import { SPONSORS_SECTION_ID } from './EventTitleSponsor';

/** The rank drawn large, on its own line. */
const LEAD_LEVEL = 1;

/** Sponsors sorted the way they were arranged within their own level. */
const byOrder = (a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0);

/**
 * Bucket the non-lead sponsors into blocks that share a level AND a caption.
 *
 * Returns `[{ key, caption, sponsors }]`, lowest level first with the
 * unlevelled block last. Two sponsors at level 3 both captioned "Gold
 * Sponsors" are one block; a level that mixes "Powered By" with "Hydration
 * Partner" splits into two, because those are relationships rather than a
 * rank and folding them would lose which sponsor is which.
 */
export function groupSponsors(sponsors) {
  const items = Array.isArray(sponsors) ? sponsors : [];
  const sorted = [...items].sort((a, b) => {
    const la = parseLevel(a?.sponsor_level);
    const lb = parseLevel(b?.sponsor_level);
    // null sorts last: an unranked sponsor follows every ranked one.
    if (la !== lb) {
      if (la === null) return 1;
      if (lb === null) return -1;
      return la - lb;
    }
    return byOrder(a, b);
  });

  const blocks = new Map();
  sorted.forEach((sponsor) => {
    const level = parseLevel(sponsor?.sponsor_level);
    const label = headingKey(sponsor?.sponsor_label);
    const key = `${level === null ? '_' : level}|${label}`;
    if (!blocks.has(key)) {
      blocks.set(key, {
        key,
        // The first spelling seen, so the caption reads as the organizer
        // wrote it rather than as the folded match key.
        caption: String(sponsor?.sponsor_label || '').trim(),
        sponsors: [],
      });
    }
    blocks.get(key).sponsors.push(sponsor);
  });

  return [...blocks.values()];
}

/** Whether this list has anything to say beyond a flat wall of logos. */
const hasAnyLabelling = (sponsors) => sponsors.some((s) => headingKey(s?.sponsor_label));

export function SponsorLockup({ sponsor, resolveUrl, caption }) {
  const logoUrl = resolveUrl(sponsor?.logo_url);
  const href = resolveUrl(sponsor?.website_url) || undefined;
  const name = sponsor?.name;

  return (
    <a className="evb-splock" href={href}>
      {caption && <span className="evb-splock__c">{caption}</span>}
      <span className="evb-splock__p">
        {logoUrl
          ? <img className="evb-splock__m" src={logoUrl} alt="" />
          /* No logo: the name becomes the mark. It sits on the plate at the
             size the logo would have had, so the row keeps its rhythm. */
          : <span className="evb-splock__w" title={name}>{name}</span>}
      </span>
      {/* With a logo the name is printed under it — most sponsor marks are
          symbols, so a logo alone leaves a visitor looking at a shape they
          cannot name. With no logo the plate already carries it. */}
      {logoUrl && <span className="evb-splock__n" title={name}>{name}</span>}
    </a>
  );
}

export default function EventSponsors({ sponsors, resolveUrl }) {
  const items = Array.isArray(sponsors) ? sponsors : [];
  // Nothing labelled anywhere means a pre-existing event: draw the plates
  // with no captions rather than inventing wording over a list that never
  // had any.
  const bare = !hasAnyLabelling(items);

  const lead = items
    .filter((s) => parseLevel(s?.sponsor_level) === LEAD_LEVEL)
    .sort(byOrder);
  const rest = items.filter((s) => parseLevel(s?.sponsor_level) !== LEAD_LEVEL);
  const blocks = groupSponsors(rest);

  return (
    /* The id is the destination of the hero strip's partners button, and
       tabIndex lets that button move focus here as well as the viewport. */
    <section className="evb-sec" id={SPONSORS_SECTION_ID} tabIndex={-1}>
      <h2 className="evb-h">Sponsors</h2>

      {items.length === 0 && <p className="evb-note">No sponsors listed.</p>}

      {lead.length > 0 && (
        <div className="evb-spwall evb-spwall--lead">
          {lead.map((sponsor, index) => (
            <SponsorLockup
              key={sponsor?.id ?? `lead-${index}`}
              sponsor={sponsor}
              resolveUrl={resolveUrl}
              caption={bare ? '' : String(sponsor?.sponsor_label || '').trim()}
            />
          ))}
        </div>
      )}

      {/* The rule only earns its height when there is something on both sides
          of it. */}
      {lead.length > 0 && blocks.length > 0 && (
        <p className="evb-spsplit"><span>And our partners</span></p>
      )}

      {blocks.length > 0 && (
        <div className="evb-spwall">
          {blocks.map((block) => (
            <div className="evb-spblock" key={block.key}>
              {!bare && (
                <p className="evb-spblock__k">{block.caption || 'Sponsors'}</p>
              )}
              <div className="evb-spblock__r">
                {block.sponsors.map((sponsor, index) => (
                  <SponsorLockup
                    key={sponsor?.id ?? `${block.key}-${index}`}
                    sponsor={sponsor}
                    resolveUrl={resolveUrl}
                    caption=""
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
